import { useMemo, useRef } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCurpExtraction } from '../../features/onboarding/hooks/useCurpExtraction';
import {
	getRfcPrefillService,
	type RfcPrefillResponse,
} from '../services/rfc.service';
import { logger } from '../utils/logger';
import { hasExtractionEvidence } from './lib/extractionEvidence';
import { resolveRfcPrefill, type RfcPrefillState } from './lib/rfcPrefill';

const POLL_INTERVAL_MS = 2_500;
const POLL_DEADLINE_MS = 5 * 60_000;

/**
 * El RFC con el que se prellenan las pantallas de vinculación con el SAT.
 * Solo LEE: consulta a user-management-engine y, como respaldo, el estado de la
 * extracción por CURP que ya observa `CurpExtractionContext`. Nunca dispara
 * extracciones. Mientras esa extracción corre no repite la consulta (el
 * respaldo cubre ese tiempo) y consulta una vez más cada vez que cambia su
 * estado. Sin extracción en curso, mientras el servidor diga `pending`, repite
 * la consulta cada POLL_INTERVAL_MS hasta POLL_DEADLINE_MS. Requiere
 * <CurpExtractionProvider>.
 */
export const useRfcPrefill = (): RfcPrefillState => {
	const { state: extraction } = useCurpExtraction();
	const pendingSinceRef = useRef<number | null>(null);
	const extractionRunning =
		(extraction?.status === 'pending' || extraction?.status === 'running') &&
		hasExtractionEvidence(extraction);
	const pollDeadlineReached = (dataUpdatedAt: number) =>
		pendingSinceRef.current !== null &&
		dataUpdatedAt - pendingSinceRef.current >= POLL_DEADLINE_MS;

	const query = useQuery<RfcPrefillResponse>({
		queryKey: ['rfc-prefill', extraction?.status ?? null],
		queryFn: getRfcPrefillService,
		placeholderData: keepPreviousData,
		refetchInterval: (q) => {
			if (extractionRunning || q.state.data?.status !== 'pending') {
				pendingSinceRef.current = null;
				return false;
			}
			pendingSinceRef.current ??= Date.now();
			if (pollDeadlineReached(q.state.dataUpdatedAt)) {
				logger.warn('[rfc-prefill] polling deadline reached');
				return false;
			}
			return POLL_INTERVAL_MS;
		},
		retry: 1,
		staleTime: 0,
	});

	const prefill = useMemo(
		() => resolveRfcPrefill(query.data, extraction),
		[query.data, extraction],
	);
	const serverPending = query.data?.status === 'pending';
	const pollTimedOut = serverPending && pollDeadlineReached(query.dataUpdatedAt);
	const settled =
		!!query.data &&
		!query.isFetching &&
		!extractionRunning &&
		(!serverPending || pollTimedOut);
	const reason = query.data?.reason ?? null;
	const curpOrigin = query.data?.curp_origin ?? null;

	return useMemo(
		() => ({ prefill, reason, curpOrigin, settled, pollTimedOut }),
		[prefill, reason, curpOrigin, settled, pollTimedOut],
	);
};
